/**
 * Helpers behind the self-serve assay pages: the editable row model the
 * analytes table works on, its conversion to and from the `analytes`
 * definition the API validates, the worked-example check for a derived
 * row's formula, and the list of assays whose definition is owned by code.
 */
import {
  evaluateDerived,
  parseFormula,
  roundTo,
  ANALYTE_KINDS,
  ASSAY_ANALYTES_VERSION,
  type AnalyteDef,
  type AnalyteKind,
  type AssayAnalytes,
  type AssayAnalytesDynamic,
} from '@mariana/labos-client';

export { ANALYTE_KINDS };
export type { AnalyteKind, AssayAnalytes, AssayAnalytesDynamic };

/**
 * A worksheet key from an analyte name: lower snake_case, identifier-safe so
 * a formula can read it. `Fe2+` → `fe2plus`, `Cu %` → `cu_pct`,
 * `Titrant volume (mL)` → `titrant_volume_ml`, `2x dilution` → `_2x_dilution`.
 */
export function toKey(name: string): string {
  const key = name
    .trim()
    .toLowerCase()
    .replace(/\+/g, 'plus')
    .replace(/%/g, 'pct')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return /^[0-9]/.test(key) ? `_${key}` : key;
}

/** One row of the analytes table. Text fields stay text until the definition is built. */
export interface AnalyteRow {
  /** Stable React key for the row; not saved. */
  id: string;
  name: string;
  key: string;
  /** Once the operator edits the key by hand it stops following the name. */
  keyEdited: boolean;
  kind: AnalyteKind;
  unit: string;
  /** The saved definition declared `unit: null` (unitless, e.g. pH); kept so an untouched row round-trips. */
  unitNull: boolean;
  decimals: string;
  headline: boolean;
  display: boolean;
  /** input/result: starting value offered to the operator. */
  default: string;
  /** constant: the fixed value. */
  value: string;
  /** derived: the arithmetic over inputs and constants. */
  formula: string;
  /** derived without a formula (derivation in code): the declared inputs, kept as saved. */
  codeInputs: string[];
  optional: boolean;
  /** Fields the table does not edit but must not drop. */
  label?: string;
  description?: string;
  source?: AnalyteDef['source'];
  /** Worked example for a derived row: input key → typed value, and the expected result. Not saved. */
  example: Record<string, string>;
  expected: string;
  /** The formula as last saved; a changed or new formula needs a passing worked example. */
  savedFormula: string | null;
}

let rowSeq = 0;
export const nextRowId = (): string => `row-${++rowSeq}-${Date.now().toString(36)}`;

export function emptyRow(kind: AnalyteKind = 'result'): AnalyteRow {
  return {
    id: nextRowId(), name: '', key: '', keyEdited: false, kind, unit: '', unitNull: false, decimals: '',
    headline: false, display: false, default: '', value: '', formula: '', codeInputs: [], optional: false,
    example: {}, expected: '', savedFormula: null,
  };
}

export function rowFromAnalyte(a: AnalyteDef): AnalyteRow {
  return {
    ...emptyRow(a.kind),
    name: a.name,
    key: a.key,
    // A saved key is a worksheet contract: renaming the analyte must not move it.
    keyEdited: true,
    unit: a.unit ?? '',
    unitNull: a.unit === null,
    decimals: a.decimals === undefined ? '' : String(a.decimals),
    headline: a.headline === true,
    display: a.display === true,
    default: a.default === undefined ? '' : String(a.default),
    value: a.value === undefined ? '' : String(a.value),
    formula: a.formula ?? '',
    codeInputs: a.formula ? [] : (a.inputs ?? []),
    optional: a.optional === true,
    label: a.label,
    description: a.description,
    source: a.source,
    savedFormula: a.formula ?? '',
  };
}

export const rowsFromDefinition = (def: AssayAnalytes | null | undefined): AnalyteRow[] =>
  (def?.analytes ?? []).map(rowFromAnalyte);

/** Keys of the constant rows: a formula reads them without listing them as inputs. */
export const constantKeys = (rows: AnalyteRow[]): string[] =>
  rows.filter(r => r.kind === 'constant' && r.key.trim()).map(r => r.key.trim());

/**
 * The inputs a derived row declares: the identifiers its formula reads that
 * are not constants; without a formula, the inputs saved with it.
 */
export function inputsOf(row: AnalyteRow, constants: string[]): string[] {
  if (!row.formula.trim()) return row.codeInputs;
  const parsed = parseFormula(row.formula);
  if (!parsed.ok) return [];
  return parsed.formula.identifiers.filter(name => !constants.includes(name));
}

const numberOrUndefined = (text: string): number | undefined => {
  const t = text.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

/** The analyte entry a row stands for. Loose: the validator names what is wrong. */
export function analyteFromRow(row: AnalyteRow, constants: string[]): AnalyteDef {
  const a: AnalyteDef = { key: row.key.trim(), name: row.name.trim(), kind: row.kind };
  const unit = row.unit.trim();
  if (unit) a.unit = unit;
  else if (row.unitNull) a.unit = null;
  const decimals = numberOrUndefined(row.decimals);
  if (decimals !== undefined) a.decimals = decimals;
  if (row.label) a.label = row.label;
  if (row.description) a.description = row.description;
  if (row.kind !== 'constant') {
    if (row.headline) a.headline = true;
    if (row.display) a.display = true;
  }
  if (row.kind === 'derived') {
    const inputs = inputsOf(row, constants);
    if (inputs.length > 0) a.inputs = inputs;
    if (row.formula.trim()) a.formula = row.formula.trim();
    if (row.optional) a.optional = true;
  }
  if (row.kind === 'constant') {
    const value = numberOrUndefined(row.value);
    if (value !== undefined) a.value = value;
  }
  if (row.kind === 'input' || row.kind === 'result') {
    const d = numberOrUndefined(row.default);
    if (d !== undefined) a.default = d;
  }
  if (row.kind === 'input' && row.source) a.source = row.source;
  return a;
}

export interface DefinitionOptions {
  strict?: boolean;
  dynamic?: AssayAnalytesDynamic | null;
}

/** The whole definition the PATCH carries, as typed: not yet validated. */
export function definitionFromRows(rows: AnalyteRow[], opts: DefinitionOptions = {}): AssayAnalytes {
  const constants = constantKeys(rows);
  const def: AssayAnalytes = { version: ASSAY_ANALYTES_VERSION, analytes: rows.map(r => analyteFromRow(r, constants)) };
  if (opts.strict) def.strict = true;
  if (opts.dynamic) def.dynamic = opts.dynamic;
  return def;
}

export type WorkedExampleResult =
  /** The row is not derived, has no formula, or its formula is unchanged since the last save. */
  | { status: 'not-needed' }
  /** Inputs or the expected value are still blank. */
  | { status: 'incomplete'; inputs: string[] }
  | { status: 'error'; message: string }
  | { status: 'pass'; actual: number; expected: number }
  | { status: 'fail'; actual: number | null; expected: number };

/**
 * Evaluate a derived row's formula over its worked example with the same
 * evaluator every consumer uses, and compare with the expected result at the
 * row's `decimals`. `requireAll` makes every derived row with a formula need
 * an example (the New assay page); otherwise only a new or changed formula does.
 */
export function checkWorkedExample(rows: AnalyteRow[], row: AnalyteRow, requireAll = false): WorkedExampleResult {
  if (row.kind !== 'derived' || !row.formula.trim()) return { status: 'not-needed' };
  if (!requireAll && row.savedFormula !== null && row.savedFormula === row.formula.trim()) return { status: 'not-needed' };
  const parsed = parseFormula(row.formula);
  if (!parsed.ok) return { status: 'error', message: parsed.error };
  const constants = constantKeys(rows);
  const inputs = inputsOf(row, constants);
  const declared = new Set(rows.map(r => r.key.trim()).filter(Boolean));
  const unknown = parsed.formula.identifiers.find(name => !declared.has(name));
  if (unknown) return { status: 'error', message: `"${unknown}" is not a key in this table` };
  const expected = numberOrUndefined(row.expected);
  const missing = inputs.filter(k => numberOrUndefined(row.example[k] ?? '') === undefined);
  if (expected === undefined || missing.length > 0) return { status: 'incomplete', inputs };
  const def = definitionFromRows(rows);
  const analyte = analyteFromRow(row, constants);
  const actual = evaluateDerived(def, analyte, key => numberOrUndefined(row.example[key] ?? '') ?? null);
  if (actual === null) return { status: 'fail', actual: null, expected };
  const decimals = numberOrUndefined(row.decimals);
  const want = decimals === undefined ? expected : roundTo(expected, decimals);
  const pass = decimals === undefined ? Math.abs(actual - expected) <= 1e-9 * Math.max(1, Math.abs(expected)) : actual === want;
  return pass ? { status: 'pass', actual, expected } : { status: 'fail', actual, expected };
}

/** True when every derived row that needs a worked example has a passing one. */
export const workedExamplesPass = (rows: AnalyteRow[], requireAll = false): boolean =>
  rows.every(r => { const s = checkWorkedExample(rows, r, requireAll).status; return s === 'not-needed' || s === 'pass'; });

/**
 * Assays whose definition the seed writes from code, so an edit here is
 * overwritten on the next `seed-assay-analytes --overwrite`. Mirrors
 * labos/src/labos/src/cli/seed-assay-analytes.ts (codeDefinitionFor):
 * ICP by title prefix, BENCH_ASSAY_DEFINITIONS, MINING_ASSAY_TITLES
 * (lims/assays/mining/constants.ts), the Omni titles, and the Shimadzu,
 * Oil in Water, LOI, titrator, Fe3+ and Camsizer assays it routes by id.
 */
export const CODE_OWNED_ASSAY_TITLE_PREFIXES = ['ARCOS ICP'];
export const CODE_OWNED_ASSAY_TITLES = [
  // BENCH_ASSAY_DEFINITIONS
  'pH by Mettler Toledo', 'ORP by Metler Toledo', 'TSS', 'Column Weight', 'Precipitation', 'Temperature',
  // MINING_ASSAY_TITLES (the two ICP ones fall under the prefix)
  'ASCu (acid-soluble Cu)', 'CNCu (cyanide-soluble Cu)', 'Acid Consumption Test',
  // OMNI_ASSAY_TITLES
  'pH by Omni', 'ORP by Omni', 'Acid Titration by Omni', 'Fe Titration by Omni',
  // Shimadzu (ANALYSIS_TO_ASSAY_ID)
  'Shimadzu - TC', 'Shimadzu - IC', 'Shimadzu - NPOC', 'Shimadzu - TOC', 'Shimadzu - POC', 'Shimadzu - TN',
  // Oil in Water, LOI, titrator, Fe3+, Camsizer (routed by id in the seed)
  'Oil in Water', 'Loss on Ignition',
  'Carbonate & Bicarbonate in Solution (Titration)', 'Lithium Carbonate Purity (Titration)',
  'Fe3+ by Calculation',
  'Camsizer - Particle Size - Lithium Carbonate', 'Camsizer - Particle Size - Copper Wire', 'Camsizer - Particle Size - Copper Powder',
];

const squash = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
const codeOwned = new Set(CODE_OWNED_ASSAY_TITLES.map(squash));

export function isManagedInCode(title: string | null | undefined): boolean {
  if (!title) return false;
  const t = squash(title);
  return CODE_OWNED_ASSAY_TITLE_PREFIXES.some(p => t.startsWith(p.toLowerCase())) || codeOwned.has(t);
}
